import { Routes, Route, Navigate, useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { useEffect, useState, useRef, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  login,
  logout,
  listWorkspaces,
  listSurveys,
  publishSurvey,
  openDiffusion,
  closeDiffusion,
  archiveSurvey,
  unarchiveSurvey,
  deleteSurvey,
  getResponsesAggregate,
  getIdentifiedResponses,
  downloadExport,
  getStoredToken,
  getStoredEmail,
  getErrorMessage,
  type Workspace,
  type SurveySummary,
  type IdentifiedResponseRow,
} from '@/lib/api';
import {
  Badge,
  Avatar,
  EmptyState,
  Loading,
  PageHeader,
  Icon,
  ConfirmDialog,
  ActionMenu,
  QrCodeDialog,
  type MenuAction,
} from '@/components/ui';
import NewSurveyPage from './NewSurveyPage';
import SurveyEditorPage from './SurveyEditorPage';
import MembersPage from './MembersPage';
import WorkspaceSettingsPage from './WorkspaceSettingsPage';
import InvitePage from './InvitePage';
import ConnectorsPage from './ConnectorsPage';
import OAuthConsentPage from './OAuthConsentPage';
import { ShareSurveyDialog } from './ShareSurveyDialog';

/**
 * Application créateur — refonte UI/UX v2 (PIXEL — 2026-05-20).
 *
 * Routes :
 * - /login                                                  — login JWT
 * - /workspaces                                             — sélection workspace
 * - /workspaces/:id/surveys                                 — liste enquêtes
 * - /workspaces/:id/surveys/new                             — création
 * - /workspaces/:id/surveys/:surveyId/edit                  — éditeur structuré
 * - /workspaces/:id/surveys/:slug/responses                 — agrégats k≥5
 *
 * Principes UX :
 *  - 1 topbar persistante avec brand, workspace context, user menu
 *  - Hiérarchie typographique forte (h1 page, h2 section, h3 carte)
 *  - Cards-as-content avec status/identityMode lisibles
 *  - Loading + Empty states explicites (pas de placeholder vide)
 *  - Tous les sélecteurs E2E (labels, rôles, headings) sont préservés
 */
export default function CreatorApp(): JSX.Element {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/workspaces" replace />} />
      <Route path="/login" element={<LoginPage />} />
      {/* Acceptation d'invitation — publique (l'invité·e n'a pas encore de session). */}
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/workspaces" element={<RequireAuth><WorkspacesListPage /></RequireAuth>} />
      <Route
        path="/workspaces/:id/surveys"
        element={<RequireAuth><AppShell><SurveysListPage /></AppShell></RequireAuth>}
      />
      <Route
        path="/workspaces/:id/surveys/new"
        element={<RequireAuth><AppShell><NewSurveyPage /></AppShell></RequireAuth>}
      />
      <Route
        path="/workspaces/:id/surveys/:surveyId/edit"
        element={<RequireAuth><AppShell><SurveyEditorPage /></AppShell></RequireAuth>}
      />
      <Route
        path="/workspaces/:id/surveys/:slug/responses"
        element={<RequireAuth><AppShell><ResponsesPage /></AppShell></RequireAuth>}
      />
      <Route
        path="/workspaces/:id/members"
        element={<RequireAuth><AppShell><MembersPage /></AppShell></RequireAuth>}
      />
      <Route
        path="/workspaces/:id/settings"
        element={<RequireAuth><AppShell><WorkspaceSettingsPage /></AppShell></RequireAuth>}
      />
      {/* MCP / connecteurs IA (MCP-P1.4) */}
      <Route
        path="/workspaces/:id/connectors"
        element={<RequireAuth><AppShell><ConnectorsPage /></AppShell></RequireAuth>}
      />
      {/* Écran de consent OAuth — atterri ici depuis le client MCP externe.
          Pas de AppShell (look "natif" OAuth, plein écran).
          On accepte les 2 paths standard : `/oauth/authorize` (RFC 6749) ET
          `/oauth/consent` (legacy interne) — Claude.ai utilise authorize. */}
      <Route
        path="/oauth/authorize"
        element={<RequireAuth><OAuthConsentPage /></RequireAuth>}
      />
      <Route
        path="/oauth/consent"
        element={<RequireAuth><OAuthConsentPage /></RequireAuth>}
      />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

// ----- Auth helpers ---------------------------------------------------------

function useAuth() {
  const [token, setToken] = useState<string | null>(() => getStoredToken());
  useEffect(() => {
    const handler = (): void => setToken(getStoredToken());
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);
  return { token, refresh: (): void => setToken(getStoredToken()) };
}

function RequireAuth({ children }: { children: React.ReactNode }): JSX.Element {
  const { token } = useAuth();
  if (!token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// ----- AppShell : topbar persistante + container ----------------------------

function AppShell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="min-h-screen bg-bg flex flex-col">
      <Topbar />
      <main className="flex-1 page-container animate-fade-in">{children}</main>
      <footer className="border-t border-border bg-bg-deep/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 text-xs text-text-muted flex flex-wrap items-center justify-between gap-2">
          <span>WebForge Survey · hébergement UE · RGPD by design</span>
          <span>Réservé aux créateurs — les répondants restent anonymes.</span>
        </div>
      </footer>
    </div>
  );
}

function Topbar(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id?: string }>();

  // Workspace courant (depuis l'URL) — pour breadcrumb.
  const wsQuery = useQuery({
    queryKey: ['workspaces'],
    queryFn: listWorkspaces,
    staleTime: 30_000,
  });
  const currentWs = wsQuery.data?.find((w) => w.id === params.id);
  // Utilisateur connecté (e-mail mémorisé au login) — plus de libellé codé en dur.
  const userEmail = getStoredEmail() ?? 'Compte créateur';

  // Menu utilisateur (dropdown léger contrôlé)
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  return (
    <header className="topbar">
      <div className="topbar-inner">
        <Link to="/workspaces" className="brand-mark" aria-label="WebForge Survey — accueil">
          <span className="brand-dot">W</span>
          <span className="hidden sm:inline">WebForge Survey</span>
        </Link>

        {/* Breadcrumbs — workspace > section */}
        <nav className="crumbs ml-2 hidden sm:flex" aria-label="Fil d'Ariane">
          <Icon.ChevronRight className="text-text-subtle" />
          {currentWs ? (
            <Link to={`/workspaces/${currentWs.id}/surveys`} className="font-medium text-text">
              {currentWs.name}
            </Link>
          ) : (
            <Link to="/workspaces" className="font-medium text-text">Workspaces</Link>
          )}
          {/* Sub-segment */}
          {location.pathname.endsWith('/new') && (
            <>
              <Icon.ChevronRight className="text-text-subtle" />
              <span>Nouvelle enquête</span>
            </>
          )}
          {location.pathname.endsWith('/edit') && (
            <>
              <Icon.ChevronRight className="text-text-subtle" />
              <span>Éditeur</span>
            </>
          )}
          {location.pathname.endsWith('/responses') && (
            <>
              <Icon.ChevronRight className="text-text-subtle" />
              <span>Réponses</span>
            </>
          )}
          {location.pathname.endsWith('/members') && (
            <>
              <Icon.ChevronRight className="text-text-subtle" />
              <span>Membres</span>
            </>
          )}
          {location.pathname.endsWith('/settings') && (
            <>
              <Icon.ChevronRight className="text-text-subtle" />
              <span>Paramètres</span>
            </>
          )}
        </nav>

        <div className="flex-1" />

        {/* User menu */}
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-full pl-1.5 pr-3 py-1 hover:bg-surface-2 transition-colors"
            style={{ minHeight: 36 }}
            onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <Avatar name={userEmail} />
            <span className="text-sm font-medium text-text hidden sm:inline max-w-[180px] truncate">{userEmail}</span>
            <Icon.ChevronDown className="text-text-muted" />
          </button>
          {menuOpen && (
            <div
              role="menu"
              className="absolute right-0 mt-2 w-56 bg-surface rounded-xl border border-border shadow-elevated overflow-hidden animate-fade-in"
            >
              <div className="px-4 py-3 border-b border-border">
                <p className="text-sm font-medium text-text">Compte créateur</p>
                <p className="text-xs text-text-muted truncate">{userEmail}</p>
              </div>
              {params.id && (
                <>
                  <Link
                    to={`/workspaces/${params.id}/settings`}
                    role="menuitem"
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-surface-2 flex items-center gap-2 text-text"
                    onClick={() => setMenuOpen(false)}
                  >
                    <Icon.Settings />
                    Paramètres du workspace
                  </Link>
                  <Link
                    to={`/workspaces/${params.id}/connectors`}
                    role="menuitem"
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-surface-2 flex items-center gap-2 text-text"
                    onClick={() => setMenuOpen(false)}
                  >
                    {/* Pas d'icone dédiée dans le set actuel ; on réutilise Settings
                        avec un libellé clair. (Un Icon.Plug pourra être ajouté en P1.5.) */}
                    <Icon.Settings />
                    Connecteurs IA
                  </Link>
                </>
              )}
              <button
                type="button"
                role="menuitem"
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-surface-2 flex items-center gap-2 text-danger-text border-t border-border"
                onClick={() => {
                  logout();
                  navigate('/login');
                }}
              >
                <Icon.Logout />
                Se déconnecter
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

// ----- /login ---------------------------------------------------------------

function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email, password);
      navigate('/workspaces');
    } catch (err) {
      setError(getErrorMessage(err, 'Identifiants invalides.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-bg flex flex-col">
      <div className="flex-1 grid lg:grid-cols-2">
        {/* Côté brand */}
        <aside className="hidden lg:flex flex-col justify-between px-12 py-10 bg-primary-strong text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 pointer-events-none" aria-hidden="true">
            <div className="absolute -top-32 -left-20 w-96 h-96 rounded-full bg-white blur-3xl" />
            <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-white blur-3xl" />
          </div>
          <div className="relative">
            <span className="inline-flex items-center gap-2 font-semibold text-base">
              <span className="brand-dot bg-white text-primary-strong">W</span>
              WebForge Survey
            </span>
          </div>
          <div className="relative max-w-md">
            <h2 className="text-white text-3xl leading-tight font-semibold tracking-tight">
              Concevez des études sensibles, sans compromis sur l'anonymat.
            </h2>
            <p className="mt-4 text-white/85 leading-relaxed text-[15px]">
              WebForge Survey est un studio d'enquêtes RGPD-natif pour la recherche
              traitant des données de l'article 9 (santé, vie sexuelle, infractions).
              Les répondants restent strictement anonymes — par construction.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-white/90">
              <li className="flex items-start gap-2"><Icon.Shield size={18} /> Chiffrement applicatif AES-256-GCM au repos</li>
              <li className="flex items-start gap-2"><Icon.Globe size={18} /> Hébergement UE, aucune ressource tierce</li>
              <li className="flex items-start gap-2"><Icon.Users size={18} /> Multi-tenant : workspaces + rôles fins</li>
            </ul>
          </div>
          <p className="relative text-xs text-white/60">© WebForge Survey — Studio de recherche</p>
        </aside>

        {/* Form */}
        <section className="flex items-center justify-center px-4 sm:px-8 py-10">
          <form onSubmit={handleSubmit} className="w-full max-w-md space-y-5" noValidate>
            <header className="space-y-2">
              <div className="lg:hidden mb-4">
                <span className="brand-mark">
                  <span className="brand-dot">W</span> WebForge Survey
                </span>
              </div>
              <h1>Connexion créateur</h1>
              <p className="text-sm text-text-muted">
                Réservé aux comptes ayant créé un workspace. Vous accédez aux
                enquêtes dont vous êtes responsable de traitement ou auxquelles
                vous avez été invité·e.
              </p>
            </header>

            <div className="card space-y-4">
              <div className="field">
                <label htmlFor="login-email" className="field-label">Adresse e-mail</label>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  required
                  className="input-base"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@institution.fr"
                />
              </div>
              <div className="field">
                <label htmlFor="login-pw" className="field-label">Mot de passe</label>
                <input
                  id="login-pw"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="input-base"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              {error && (
                <p role="alert" className="field-error">
                  <Icon.AlertTriangle />
                  <span>{error}</span>
                </p>
              )}

              <button type="submit" disabled={loading} className="btn-primary w-full btn-lg">
                {loading ? 'Connexion…' : 'Se connecter'}
              </button>
            </div>

            <p className="text-xs text-text-muted text-center">
              Pas de compte ? Les comptes créateurs sont créés via invitation de votre référent
              workspace.
            </p>
          </form>
        </section>
      </div>
    </main>
  );
}

// ----- /workspaces ----------------------------------------------------------

function WorkspacesListPage(): JSX.Element {
  const navigate = useNavigate();
  const wsQuery = useQuery({ queryKey: ['workspaces'], queryFn: listWorkspaces });

  // Workspace inhérent au compte : si le créateur n'a qu'un seul workspace,
  // on saute l'écran de sélection et on va directement à ses enquêtes.
  useEffect(() => {
    if (wsQuery.data && wsQuery.data.length === 1) {
      navigate(`/workspaces/${wsQuery.data[0]!.id}/surveys`, { replace: true });
    }
  }, [wsQuery.data, navigate]);

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      <Topbar />
      <main className="flex-1 page-container animate-fade-in">
        <PageHeader
          title="Vos workspaces"
          subtitle="Vous appartenez à plusieurs espaces. Sélectionnez celui sur lequel travailler — chacun a son propre responsable de traitement et sa rétention."
        />

        {wsQuery.isPending && <Loading label="Chargement des workspaces…" />}
        {wsQuery.isError && (
          <p role="alert" className="field-error">
            <Icon.AlertTriangle />
            <span>{getErrorMessage(wsQuery.error)}</span>
          </p>
        )}
        {wsQuery.data && wsQuery.data.length === 0 && (
          <EmptyState
            icon={<Icon.Folder size={36} />}
            title="Aucun workspace pour le moment"
            description="Vous n'avez accès à aucun workspace. Demandez une invitation à un responsable."
          />
        )}
        {wsQuery.data && wsQuery.data.length > 0 && (
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {wsQuery.data.map((w) => (
              <li key={w.id}>
                <WorkspaceCard ws={w} />
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}

function WorkspaceCard({ ws }: { ws: Workspace }): JSX.Element {
  return (
    <Link
      to={`/workspaces/${ws.id}/surveys`}
      className="card card-hover block group focus-visible:border-primary"
    >
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-xl bg-primary-soft text-primary-strong inline-flex items-center justify-center" aria-hidden="true">
          <Icon.Folder size={22} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg group-hover:text-primary transition-colors">{ws.name}</h2>
          <p className="text-xs text-text-muted mt-1">Rôle&nbsp;: <span className="font-medium text-text">{ws.role}</span></p>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-xs text-text-muted">
        <span className="inline-flex items-center gap-1.5">
          <Icon.Shield size={14} /> RGPD by design
        </span>
        <span className="inline-flex items-center gap-1 text-primary font-medium">
          Ouvrir <Icon.ChevronRight size={14} />
        </span>
      </div>
    </Link>
  );
}

// ----- /workspaces/:id/surveys ----------------------------------------------

type DialogState =
  | { kind: 'none' }
  | { kind: 'close'; survey: SurveySummary }
  | { kind: 'archive'; survey: SurveySummary }
  | { kind: 'unarchive'; survey: SurveySummary }
  | { kind: 'delete'; survey: SurveySummary };

function SurveysListPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'active' | 'archived'>('active');
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });
  const [shareTarget, setShareTarget] = useState<SurveySummary | null>(null);
  const [qrTarget, setQrTarget] = useState<SurveySummary | null>(null);

  // Réinitialise la page quand on change d'onglet.
  useEffect(() => setPage(1), [tab]);
  const [actionError, setActionError] = useState<string | null>(null);

  const surveys = useQuery({
    queryKey: ['surveys', id],
    queryFn: () => listSurveys(id ?? ''),
    enabled: Boolean(id),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['surveys', id] });
  };

  const publishMutation = useMutation({
    mutationFn: (surveyId: string) => publishSurvey(id ?? '', surveyId),
    onSuccess: invalidate,
  });

  const lifecycleMutation = useMutation({
    mutationFn: async (vars: { action: DialogState['kind']; surveyId: string }) => {
      if (vars.action === 'close') return closeDiffusion(id ?? '', vars.surveyId);
      if (vars.action === 'archive') return archiveSurvey(id ?? '', vars.surveyId);
      if (vars.action === 'unarchive') return unarchiveSurvey(id ?? '', vars.surveyId);
      if (vars.action === 'delete') return deleteSurvey(id ?? '', vars.surveyId, { force: true });
      return undefined;
    },
    onSuccess: () => {
      invalidate();
      setDialog({ kind: 'none' });
      setActionError(null);
    },
    onError: (err) => setActionError(getErrorMessage(err)),
  });

  // Rouvrir la diffusion d'une enquête fermée (closed → published). Action directe
  // (pas de dialogue) ; l'erreur éventuelle (ex. AIPD requise) est remontée.
  const reopenMutation = useMutation({
    mutationFn: (surveyId: string) => openDiffusion(id ?? '', surveyId),
    onSuccess: () => { invalidate(); setActionError(null); },
    onError: (err) => setActionError(getErrorMessage(err)),
  });

  if (!id) return <p>Workspace introuvable.</p>;

  const all = surveys.data ?? [];
  const activeList = all.filter((s) => s.status !== 'archived');
  const archivedList = all.filter((s) => s.status === 'archived');
  // Si plus aucune archivée, on bascule l'affichage sur les actives (l'onglet
  // "Archivées" disparaît, donc le state 'archived' n'aurait plus de sens).
  const effectiveTab = tab === 'archived' && archivedList.length === 0 ? 'active' : tab;
  const filtered = effectiveTab === 'active' ? activeList : archivedList;
  const published = activeList.filter((s) => s.status === 'published').length;

  // Pagination (client-side) : au-delà de PAGE_SIZE, on pagine pour la lisibilité.
  const PAGE_SIZE = 15;
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const shown = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <>
      <PageHeader
        title="Enquêtes"
        subtitle={
          all.length === 0
            ? 'Créez votre première enquête pour démarrer.'
            : `${activeList.length} active${activeList.length > 1 ? 's' : ''} · ${published} publiée${published > 1 ? 's' : ''}${archivedList.length ? ` · ${archivedList.length} archivée${archivedList.length > 1 ? 's' : ''}` : ''}`
        }
        actions={
          <div className="flex items-center gap-2">
            <Link to={`/workspaces/${id}/settings`} className="btn-secondary" aria-label="Paramètres du workspace">
              <Icon.Settings /> <span className="hidden sm:inline">Paramètres</span>
            </Link>
            <Link to={`/workspaces/${id}/members`} className="btn-secondary">
              <Icon.Users /> <span className="hidden sm:inline">Membres</span>
            </Link>
            <Link to={`/workspaces/${id}/surveys/new`} className="btn-primary">
              + Nouvelle enquête
            </Link>
          </div>
        }
      />

      {/* Onglets actives / archivées */}
      {archivedList.length > 0 && (
        <div className="flex items-center gap-1 mb-4 border-b border-border">
          <TabButton active={effectiveTab === 'active'} onClick={() => setTab('active')}>
            Actives ({activeList.length})
          </TabButton>
          <TabButton active={effectiveTab === 'archived'} onClick={() => setTab('archived')}>
            Archivées ({archivedList.length})
          </TabButton>
        </div>
      )}

      {actionError && (
        <p role="alert" className="field-error mb-4">
          <Icon.AlertTriangle />
          <span>{actionError}</span>
        </p>
      )}

      {surveys.isPending && <Loading label="Chargement des enquêtes…" />}
      {surveys.isError && (
        <p role="alert" className="field-error">
          <Icon.AlertTriangle />
          <span>{getErrorMessage(surveys.error)}</span>
        </p>
      )}
      {surveys.data && all.length === 0 && (
        <EmptyState
          icon={<Icon.ChartBar size={36} />}
          title="Aucune enquête dans ce workspace"
          description="Démarrez avec un modèle vide ou importez un JSON V16 existant."
          action={
            <Link to={`/workspaces/${id}/surveys/new`} className="btn-primary">
              + Créer une enquête
            </Link>
          }
        />
      )}
      {surveys.data && all.length > 0 && shown.length === 0 && (
        <EmptyState
          icon={<Icon.Folder size={36} />}
          title={tab === 'archived' ? 'Aucune enquête archivée' : 'Aucune enquête active'}
          description={
            tab === 'archived'
              ? 'Les enquêtes que vous archivez apparaîtront ici.'
              : 'Toutes vos enquêtes sont archivées.'
          }
        />
      )}
      {surveys.data && shown.length > 0 && (
        <div className="card card-flush overflow-hidden">
          <div className="hidden lg:grid grid-cols-[minmax(0,1fr),104px,140px,104px,184px] gap-4 items-center px-5 py-3 border-b border-border bg-bg-deep/40 text-xs uppercase tracking-wide text-text-muted font-medium">
            <span>Enquête</span>
            <span>Statut</span>
            <span>Identité</span>
            <span>Réponses</span>
            <span className="text-right">Actions</span>
          </div>
          <ul>
            {shown.map((s) => (
              <SurveyRow
                key={s.id}
                survey={s}
                workspaceId={id}
                onPublish={() => publishMutation.mutate(s.id)}
                onReopen={() => reopenMutation.mutate(s.id)}
                onClose={() => setDialog({ kind: 'close', survey: s })}
                onArchive={() => setDialog({ kind: 'archive', survey: s })}
                onUnarchive={() => setDialog({ kind: 'unarchive', survey: s })}
                onDelete={() => setDialog({ kind: 'delete', survey: s })}
                onShare={() => setShareTarget(s)}
                onQr={() => setQrTarget(s)}
              />
            ))}
          </ul>
          {pageCount > 1 && (
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-border bg-bg-deep/30 text-sm">
              <span className="text-text-muted">
                {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} sur {filtered.length}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <Icon.ChevronRight className="rotate-180" size={14} /> Précédent
                </button>
                <span className="text-text-muted tabular-nums">Page {safePage} / {pageCount}</span>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={safePage >= pageCount}
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                >
                  Suivant <Icon.ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* --- Dialogues de confirmation --- */}
      <ConfirmDialog
        open={dialog.kind === 'close'}
        title="Stopper la diffusion ?"
        description={
          dialog.kind === 'close' ? (
            <>L'enquête <strong>{dialog.survey.title}</strong> ne sera plus accessible
            publiquement. Les réponses déjà reçues sont conservées. Vous pourrez la
            rediffuser plus tard.</>
          ) : undefined
        }
        confirmLabel="Stopper la diffusion"
        busy={lifecycleMutation.isPending}
        onCancel={() => setDialog({ kind: 'none' })}
        onConfirm={() =>
          dialog.kind === 'close' &&
          lifecycleMutation.mutate({ action: 'close', surveyId: dialog.survey.id })
        }
      />

      <ConfirmDialog
        open={dialog.kind === 'archive'}
        title="Archiver cette enquête ?"
        description={
          dialog.kind === 'archive' ? (
            <>L'enquête <strong>{dialog.survey.title}</strong> sera masquée de la liste
            active. Aucune donnée n'est supprimée — vous pourrez la désarchiver à tout
            moment.</>
          ) : undefined
        }
        confirmLabel="Archiver"
        busy={lifecycleMutation.isPending}
        onCancel={() => setDialog({ kind: 'none' })}
        onConfirm={() =>
          dialog.kind === 'archive' &&
          lifecycleMutation.mutate({ action: 'archive', surveyId: dialog.survey.id })
        }
      />

      <ConfirmDialog
        open={dialog.kind === 'unarchive'}
        title="Désarchiver cette enquête ?"
        description={
          dialog.kind === 'unarchive' ? (
            <>L'enquête <strong>{dialog.survey.title}</strong> reviendra dans la liste
            active (état fermé). Vous pourrez la rediffuser ensuite.</>
          ) : undefined
        }
        confirmLabel="Désarchiver"
        busy={lifecycleMutation.isPending}
        onCancel={() => setDialog({ kind: 'none' })}
        onConfirm={() =>
          dialog.kind === 'unarchive' &&
          lifecycleMutation.mutate({ action: 'unarchive', surveyId: dialog.survey.id })
        }
      />

      <ConfirmDialog
        open={dialog.kind === 'delete'}
        tone="danger"
        title="Supprimer définitivement ?"
        description={
          dialog.kind === 'delete' ? (
            <>
              Cette action est <strong>irréversible</strong>. L'enquête{' '}
              <strong>{dialog.survey.title}</strong>
              {dialog.survey.responsesCount > 0 ? (
                <> et ses <strong>{dialog.survey.responsesCount} réponse
                {dialog.survey.responsesCount > 1 ? 's' : ''} chiffrée
                {dialog.survey.responsesCount > 1 ? 's' : ''}</strong> seront
                définitivement détruites.</>
              ) : (
                <> sera définitivement supprimée.</>
              )}
            </>
          ) : undefined
        }
        confirmLabel="Supprimer définitivement"
        confirmPhrase={dialog.kind === 'delete' ? dialog.survey.title : undefined}
        busy={lifecycleMutation.isPending}
        onCancel={() => setDialog({ kind: 'none' })}
        onConfirm={() =>
          dialog.kind === 'delete' &&
          lifecycleMutation.mutate({ action: 'delete', surveyId: dialog.survey.id })
        }
      />

      {shareTarget && (
        <ShareSurveyDialog
          open
          workspaceId={id}
          surveyId={shareTarget.id}
          surveyTitle={shareTarget.title}
          onClose={() => setShareTarget(null)}
        />
      )}
      <QrCodeDialog
        open={qrTarget !== null}
        // Le QR pointe directement vers le wizard (parcours 1 question/écran)
        // pour que les répondants atterrissent sur la bonne UI sans passer par
        // une redirection (utile pour les anciens QR déjà imprimés, mais plus
        // propre quand le QR est généré).
        url={qrTarget ? `${window.location.origin}/s/${qrTarget.slug}/wizard` : ''}
        title={qrTarget ? `QR — ${qrTarget.title}` : 'QR code'}
        onClose={() => setQrTarget(null)}
      />
    </>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
        active
          ? 'border-primary text-primary'
          : 'border-transparent text-text-muted hover:text-text'
      }`}
    >
      {children}
    </button>
  );
}

function SurveyRow({
  survey,
  workspaceId,
  onPublish,
  onReopen,
  onClose,
  onArchive,
  onUnarchive,
  onDelete,
  onShare,
  onQr,
}: {
  survey: SurveySummary;
  workspaceId: string;
  onPublish: () => void;
  onReopen: () => void;
  onClose: () => void;
  onArchive: () => void;
  onUnarchive: () => void;
  onDelete: () => void;
  onShare: () => void;
  onQr: () => void;
}): JSX.Element {
  const idBadge = identityBadge(survey.identityMode);
  const stBadge = statusBadge(survey.status);

  // URL publique du parcours répondant : pointe sur le wizard par défaut.
  const publicUrl = `/s/${survey.slug}/wizard`;
  // Menu d'actions contextuel selon le statut. Les actions secondaires
  // (publier, lien public, diffusion, archivage, suppression) y sont
  // regroupées pour garder un set de boutons visibles IDENTIQUE sur toutes
  // les lignes → colonnes parfaitement alignées.
  const menuActions: MenuAction[] = [
    {
      label: 'Publier',
      icon: <Icon.Check size={16} />,
      onClick: onPublish,
      hidden: survey.status !== 'draft',
    },
    {
      label: 'Rouvrir la diffusion',
      icon: <Icon.Globe size={16} />,
      onClick: onReopen,
      hidden: survey.status !== 'closed',
    },
    {
      label: 'Ouvrir le lien public',
      icon: <Icon.ExternalLink size={16} />,
      onClick: () => window.open(publicUrl, '_blank', 'noopener'),
      hidden: survey.status !== 'published',
    },
    {
      label: 'Stopper la diffusion',
      icon: <Icon.Globe size={16} />,
      onClick: onClose,
      hidden: survey.status !== 'published',
    },
    {
      label: 'Archiver',
      icon: <Icon.Folder size={16} />,
      onClick: onArchive,
      hidden: survey.status === 'archived',
    },
    {
      label: 'Désarchiver',
      icon: <Icon.Folder size={16} />,
      onClick: onUnarchive,
      hidden: survey.status !== 'archived',
    },
    {
      label: 'QR code',
      icon: <Icon.ExternalLink size={16} />,
      onClick: onQr,
      hidden: survey.status !== 'published',
    },
    {
      label: 'Partager…',
      icon: <Icon.Users size={16} />,
      onClick: onShare,
    },
    {
      label: 'Supprimer…',
      icon: <Icon.Trash size={16} />,
      onClick: onDelete,
      tone: 'danger',
    },
  ];

  const editLink = `/workspaces/${workspaceId}/surveys/${survey.id}/edit`;
  const responsesLink = `/workspaces/${workspaceId}/surveys/${survey.slug}/responses`;

  return (
    <li className="border-b border-border last:border-b-0 hover:bg-surface-2/60 transition-colors">
      {/* Desktop layout : grid 5 colonnes aligné sur le header.
          Largeurs FIXES (chaque <li> est son propre grid) → toutes les lignes
          partagent les mêmes pistes, donc les colonnes s'alignent. */}
      <div className="hidden lg:grid grid-cols-[minmax(0,1fr),104px,140px,104px,184px] gap-4 items-center px-5 py-4">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold truncate">{survey.title}</h3>
          <p className="text-xs text-text-muted mt-1 truncate">
            <span className="font-mono">
              /{survey.slug} · v{survey.definitionVersion === 0 ? '—' : survey.definitionVersion}
            </span>
            {survey.createdAt && (
              <span className="text-text-subtle"> · créée le {formatDate(survey.createdAt)}</span>
            )}
            <span className="text-text-subtle"> · {lastResponseLabel(survey.lastResponseAt)}</span>
          </p>
        </div>

        <div>
          <Badge tone={stBadge.tone}>{stBadge.label}</Badge>
        </div>

        <div>
          <Badge tone={idBadge.tone} icon={idBadge.tone === 'lock' ? <Icon.Lockfilled /> : undefined}>
            {idBadge.label}
          </Badge>
        </div>

        <div className="text-sm text-text-muted tabular-nums">
          <span className="font-medium text-text">{survey.responsesCount}</span>
          <span className="text-text-subtle"> répons{survey.responsesCount === 1 ? 'e' : 'es'}</span>
        </div>

        {/* Set d'actions IDENTIQUE sur chaque ligne : Éditer + Réponses + menu. */}
        <div className="flex items-center gap-1.5 justify-end">
          <Link to={editLink} className="btn-primary btn-sm">
            <Icon.Edit /> Éditer
          </Link>
          <Link
            to={responsesLink}
            className="btn-secondary btn-sm"
            title="Voir les réponses"
            aria-label="Voir les réponses"
          >
            <Icon.ChartBar />
          </Link>
          <ActionMenu actions={menuActions} label={`Actions pour ${survey.title}`} />
        </div>
      </div>

      {/* Mobile / tablette layout : card stack */}
      <div className="lg:hidden px-5 py-4 flex flex-col gap-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold">{survey.title}</h3>
            <p className="text-xs text-text-muted mt-0.5 font-mono break-all">
              /{survey.slug} · v{survey.definitionVersion === 0 ? '—' : survey.definitionVersion}
            </p>
          </div>
          <ActionMenu actions={menuActions} label={`Actions pour ${survey.title}`} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={stBadge.tone}>{stBadge.label}</Badge>
          <Badge tone={idBadge.tone} icon={idBadge.tone === 'lock' ? <Icon.Lockfilled /> : undefined}>
            {idBadge.label}
          </Badge>
          <span className="text-xs text-text-muted ml-auto tabular-nums">
            <span className="font-medium text-text">{survey.responsesCount}</span>
            {' '}répons{survey.responsesCount === 1 ? 'e' : 'es'}
          </span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Link to={editLink} className="btn-primary btn-sm flex-1 sm:flex-none">
            <Icon.Edit /> Éditer
          </Link>
          <Link to={responsesLink} className="btn-secondary btn-sm">
            <Icon.ChartBar /> Réponses
          </Link>
        </div>
      </div>
    </li>
  );
}

function identityBadge(
  mode: 'anonymous' | 'optional' | 'required',
): { label: string; tone: 'info' | 'primary' | 'lock' } {
  switch (mode) {
    case 'anonymous':
      return { label: 'Anonyme', tone: 'info' };
    case 'optional':
      return { label: 'Opt-in identité', tone: 'primary' };
    case 'required':
      return { label: 'Identifiée', tone: 'lock' };
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** « dernière réponse aujourd'hui / il y a N j » ou « aucune réponse ». */
function lastResponseLabel(iso: string | null | undefined): string {
  if (!iso) return 'aucune réponse';
  const then = new Date(iso);
  const today = new Date();
  const days = Math.floor(
    (Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()) -
      Date.UTC(then.getFullYear(), then.getMonth(), then.getDate())) /
      86_400_000,
  );
  if (days <= 0) return 'dernière réponse aujourd\'hui';
  if (days === 1) return 'dernière réponse hier';
  return `dernière réponse il y a ${days} j`;
}

function statusBadge(
  status: SurveySummary['status'],
): { label: string; tone: 'neutral' | 'success' | 'notice' | 'lock' } {
  switch (status) {
    case 'draft':
      return { label: 'Brouillon', tone: 'neutral' };
    case 'published':
      return { label: 'Publiée', tone: 'success' };
    case 'closed':
      return { label: 'Fermée', tone: 'notice' };
    case 'archived':
      return { label: 'Archivée', tone: 'lock' };
  }
}

// ----- /workspaces/:id/surveys/:slug/responses ------------------------------

function ResponsesPage(): JSX.Element {
  const { id, slug } = useParams<{ id: string; slug: string }>();
  const [exporting, setExporting] = useState<null | 'json' | 'csv'>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [tab, setTab] = useState<'aggregate' | 'identified'>('aggregate');

  // Mode d'identité (depuis la liste cachée) → conditionne l'onglet identifié.
  const surveysQ = useQuery({
    queryKey: ['surveys', id],
    queryFn: () => listSurveys(id ?? ''),
    enabled: Boolean(id),
  });
  const identityMode = surveysQ.data?.find((s) => s.slug === slug)?.identityMode;
  const canIdentify = identityMode === 'optional' || identityMode === 'required';

  const q = useQuery({
    queryKey: ['responses', id, slug],
    queryFn: () => getResponsesAggregate(id ?? '', slug ?? ''),
    enabled: Boolean(id && slug),
  });

  // Données identifiées : chargées UNIQUEMENT à l'ouverture explicite de l'onglet
  // (chaque accès est journalisé côté serveur — données art. 9).
  const identQ = useQuery({
    queryKey: ['identified', id, slug],
    queryFn: () => getIdentifiedResponses(id ?? '', slug ?? ''),
    enabled: Boolean(id && slug) && tab === 'identified' && canIdentify,
    refetchOnWindowFocus: false,
    // RGPD : données identifiées art. 9 déchiffrées → rétention client minimale.
    // Pas de cache « frais » et purge dès que la vue n'est plus active.
    staleTime: 0,
    gcTime: 0,
  });

  const doExport = async (format: 'json' | 'csv'): Promise<void> => {
    setExporting(format);
    setExportError(null);
    try {
      await downloadExport(id ?? '', slug ?? '', format);
    } catch (err) {
      setExportError(getErrorMessage(err, "L'export a échoué."));
    } finally {
      setExporting(null);
    }
  };

  if (!id || !slug) return <p>Lien invalide.</p>;

  return (
    <>
      <PageHeader
        title="Réponses"
        subtitle="Agrégats anonymisés (k ≥ 5). En modes identifiés, une vue par répondant est disponible (accès tracé)."
        back={{ to: `/workspaces/${id}/surveys`, label: 'Retour aux enquêtes' }}
        actions={
          <div className="flex items-center gap-2">
            <button type="button" className="btn-secondary" disabled={exporting !== null}
              onClick={() => void doExport('csv')}>
              {exporting === 'csv' ? 'Export…' : 'Exporter CSV'}
            </button>
            <button type="button" className="btn-secondary" disabled={exporting !== null}
              onClick={() => void doExport('json')}>
              {exporting === 'json' ? 'Export…' : 'Exporter JSON'}
            </button>
          </div>
        }
      />

      {exportError && (
        <p role="alert" className="field-error mb-4"><Icon.AlertTriangle /><span>{exportError}</span></p>
      )}
      <p className="notice text-sm mb-4">
        <Icon.AlertTriangle className="inline-block align-text-bottom mr-1" />
        L'export contient les réponses <strong>brutes</strong> (potentiellement données
        sensibles art. 9). Il inclut un <strong>codebook</strong> (variables, modalités,
        échelles). Vous êtes responsable de sa conservation et de sa transmission sécurisées.
      </p>

      {canIdentify && (
        <div className="flex items-center gap-1 mb-4 border-b border-border">
          <TabButton active={tab === 'aggregate'} onClick={() => setTab('aggregate')}>
            Agrégats (k-anonymat)
          </TabButton>
          <TabButton active={tab === 'identified'} onClick={() => setTab('identified')}>
            Vue par répondant
          </TabButton>
        </div>
      )}

      {tab === 'aggregate' && (
        <>
          {q.isPending && <Loading label="Calcul des agrégats…" />}
          {q.isError && (
            <p role="alert" className="field-error">
              <Icon.AlertTriangle />
              <span>{getErrorMessage(q.error)}</span>
            </p>
          )}
          {q.data && (
            <div className="space-y-6">
              <div className="card">
                <div className="flex items-baseline gap-3">
                  <span className="text-3xl font-semibold tabular-nums">{q.data.total}</span>
                  <span className="text-sm text-text-muted">réponse{q.data.total === 1 ? '' : 's'} reçue{q.data.total === 1 ? '' : 's'}</span>
                </div>
                <p className="info-callout mt-4 text-sm">
                  <Icon.Info className="inline-block align-text-bottom mr-1" />
                  Les valeurs avec un effectif inférieur à <strong>k = {q.data.k}</strong> sont
                  masquées (RGPD_PLAN §2.1).
                </p>
              </div>

              {q.data.rows.length === 0 ? (
                <EmptyState
                  icon={<Icon.ChartBar size={36} />}
                  title="Pas encore de données"
                  description="Les agrégats apparaîtront ici dès que des réponses seront soumises."
                />
              ) : (
                <div className="grid gap-4">
                  {q.data.rows.map((row) => {
                    const maxCount = Math.max(
                      ...Object.values(row.counts).map((v) => (v === null ? 0 : v)),
                      1,
                    );
                    return (
                      <article key={row.itemName} className="card">
                        <h3 className="text-base font-medium mb-3">{row.itemName}</h3>
                        <ul className="space-y-2">
                          {Object.entries(row.counts).map(([val, count]) => {
                            const ratio = count === null ? 0 : count / maxCount;
                            return (
                              <li key={val} className="text-sm">
                                <div className="flex items-baseline justify-between gap-3 mb-1">
                                  <span className="text-text truncate">{val}</span>
                                  <span className="text-text-muted tabular-nums">
                                    {count === null ? (
                                      <span className="inline-flex items-center gap-1 text-lock">
                                        <Icon.Lock size={12} /> masqué (effectif &lt; {q.data.k})
                                      </span>
                                    ) : (
                                      count
                                    )}
                                  </span>
                                </div>
                                <div
                                  className="h-2 rounded-full bg-surface-2 overflow-hidden"
                                  aria-hidden="true"
                                >
                                  <div
                                    className={count === null ? 'h-full bg-lock/30' : 'h-full bg-primary/70'}
                                    style={{ width: count === null ? '8%' : `${Math.max(ratio * 100, 4)}%` }}
                                  />
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {tab === 'identified' && canIdentify && (
        <div className="space-y-4">
          <p className="notice flex items-start gap-2">
            <Icon.Lock />
            <span>
              Vue <strong>identifiée</strong> : les réponses sont reliées à l'identité des
              répondants (données art. 9). <strong>Chaque consultation est journalisée.</strong>
              À utiliser uniquement dans les finalités annoncées au répondant (consentement).
            </span>
          </p>
          {identQ.isPending && <Loading label="Déchiffrement des réponses identifiées…" />}
          {identQ.isError && (
            <p role="alert" className="field-error">
              <Icon.AlertTriangle /><span>{getErrorMessage(identQ.error)}</span>
            </p>
          )}
          {identQ.data && identQ.data.rows.length === 0 && (
            <EmptyState
              icon={<Icon.Users size={36} />}
              title="Aucune réponse identifiée"
              description="Les répondants identifiés (et leurs réponses) apparaîtront ici."
            />
          )}
          {identQ.data && identQ.data.rows.length > 0 && (
            <div className="grid gap-4">
              {identQ.data.rows.map((row: IdentifiedResponseRow) => (
                <article key={row.ref} className="card">
                  <header className="flex items-center justify-between gap-3 mb-3">
                    <Badge tone="primary">{row.ref}</Badge>
                    {row.completed ? (
                      <span className="text-xs text-text-muted">
                        soumis le {row.submittedAtDay ? new Date(row.submittedAtDay).toLocaleDateString('fr-FR') : '—'}
                      </span>
                    ) : (
                      <Badge tone="notice">en cours</Badge>
                    )}
                  </header>
                  <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
                    {Object.entries(row.identity).map(([k, v]) => (
                      <div key={k} className="text-sm">
                        <dt className="text-text-muted">{k}</dt>
                        <dd className="font-medium break-words">{String(v)}</dd>
                      </div>
                    ))}
                  </dl>
                  {row.answers.length > 0 && (
                    <details className="mt-3 border-t border-border pt-3">
                      <summary className="text-sm font-medium cursor-pointer">
                        {row.answers.length} réponse{row.answers.length > 1 ? 's' : ''}
                      </summary>
                      <ul className="mt-2 space-y-1.5">
                        {row.answers.map((a) => (
                          <li key={a.itemName} className="text-sm flex flex-col sm:flex-row sm:gap-2">
                            <span className="text-text-muted sm:min-w-[40%]">{a.questionLabel}</span>
                            <span className="text-text break-words">{a.value || '—'}</span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </article>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ----- 404 ------------------------------------------------------------------

function NotFoundPage(): JSX.Element {
  return (
    <main className="min-h-screen flex items-center justify-center bg-bg p-6">
      <div className="card max-w-prose text-center">
        <h1 className="mb-3">Page introuvable</h1>
        <p className="text-text-muted">
          Cette page n'existe pas. Vous pouvez retourner à l'
          <Link to="/" className="text-primary underline">accueil</Link>.
        </p>
      </div>
    </main>
  );
}
